import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { intlLocale, useI18n } from '../lib/i18n';
import { cn, FOCUS_RING } from './ui';

/**
 * Seven days at a time, from today: the way MedEssist and most booking pages offer a day, and
 * one tap instead of a date picker. The typed date field stays under it for a day further out
 * (and for anyone who would rather type), so this is a shortcut, never the only way.
 *
 * A dot under a day says it has open times, where that is known (the step's two-week look
 * ahead, or a later week's own look); a day known to have none is quieter but still a choice,
 * because the patient may want to see that for themselves.
 */

/** `YYYY-MM-DD` plus `days`, by the calendar: no time zone is involved. */
export function addDays(date: string, days: number): string {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`, by the calendar. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/** The first day of the seven-day page that holds `date`, counting pages from `today`. */
export function pageStartFor(date: string, today: string): string {
  const offset = Math.max(daysBetween(today, date), 0);
  return addDays(today, Math.floor(offset / 7) * 7);
}

export function WeekStrip({
  today,
  start,
  selected,
  last,
  known,
  onSelect,
  onPage,
}: {
  /** Today in the branch's zone. */
  today: string;
  /** The first day shown. */
  start: string;
  /** The day chosen, if any. */
  selected: string;
  /** The last day this service can be booked. */
  last: string;
  /** What is known about each day: true, it has open times; false, none. Missing: not known. */
  known: ReadonlyMap<string, boolean>;
  onSelect: (date: string) => void;
  onPage: (start: string) => void;
}) {
  const { t, locale } = useI18n();
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);

  const format = useMemo(() => {
    const at = (options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(intlLocale(locale), { ...options, timeZone: 'UTC' });
    return {
      weekday: at({ weekday: 'short' }),
      day: at({ day: 'numeric' }),
      full: at({ weekday: 'long', month: 'long', day: 'numeric' }),
      month: at({ month: 'long', year: 'numeric' }),
    };
  }, [locale]);

  const noon = (date: string): Date => new Date(`${date}T12:00:00Z`);
  const first = noon(days[0]!);
  const end = noon(days[6]!);
  // "October 2026", or "October – November 2026" across a month's end.
  const monthLabel =
    first.getUTCMonth() === end.getUTCMonth()
      ? format.month.format(first)
      : format.month.formatRange(first, end);

  const canGoBack = start > today;
  const canGoOn = addDays(start, 7) <= last;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-base font-semibold capitalize text-heading">{monthLabel}</p>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onPage(addDays(start, -7) < today ? today : addDays(start, -7))}
            disabled={!canGoBack}
            aria-label={t('time.earlierDays')}
            className={cn(ARROW, FOCUS_RING)}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onPage(addDays(start, 7))}
            disabled={!canGoOn}
            aria-label={t('time.laterDays')}
            className={cn(ARROW, FOCUS_RING)}
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div role="group" aria-label={t('time.chooseDay')} className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {days.map((day) => {
          const isSelected = day === selected;
          const isToday = day === today;
          const out = day < today || day > last;
          const open = known.get(day);
          const full = format.full.format(noon(day));
          const named = isToday ? `${t('time.today')}, ${full}` : full;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelect(day)}
              disabled={out}
              aria-pressed={isSelected}
              aria-label={
                open === true
                  ? t('time.dayOpen', { day: named })
                  : open === false
                    ? t('time.dayClosed', { day: named })
                    : named
              }
              className={cn(
                'flex min-h-[4.5rem] flex-col items-center justify-center gap-0.5 rounded-xl border px-0.5 transition-colors',
                FOCUS_RING,
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-border bg-card text-heading hover:border-primary/60 hover:bg-accent/50',
                !isSelected && open === false && 'text-muted-foreground',
                out && 'cursor-not-allowed opacity-40 hover:border-border hover:bg-card',
              )}
            >
              <span
                className={cn(
                  'text-xs font-medium uppercase tracking-wide',
                  isSelected ? 'text-primary-foreground' : isToday ? 'text-primary-strong' : 'text-muted-foreground',
                )}
              >
                {format.weekday.format(noon(day)).replace('.', '')}
              </span>
              <span className="text-lg font-semibold leading-none tabular-nums">{format.day.format(noon(day))}</span>
              <span
                aria-hidden="true"
                className={cn(
                  'mt-1 h-1.5 w-1.5 rounded-full',
                  open ? (isSelected ? 'bg-primary-foreground' : 'bg-primary') : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

const ARROW =
  'grid h-10 w-10 place-items-center rounded-full border border-input bg-card text-heading transition-colors hover:border-primary hover:bg-accent disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground/50 disabled:hover:bg-card';
